import { LogLevel, LogService } from "@/services/logging";
import UserModule from "@/store/modules/user";
import Timeout from "await-timeout";
import { hasSalesStarted } from "./cash-float";
import { dexieDB } from "@/services/dexie-database";
import { SyncStatus } from "@/types/db/product-slot-booking";

// Indicates whether a db reset is running. ensures that we don't run multiple resets at the same time
let resetRunning = false;

/**
 * check if we are allowed to reset the database
 * Resetting the database means deleting all the data and starting from scratch.
 *
 * This is only allowed if sales are closed
 */
export function canResetDatabase() {
  if (UserModule.isColleague) {
    return true;
  }
  return !hasSalesStarted.value;
}

/**
 * Reset the database growing collections (Dexie tables)
 */
export async function resetGrowingCollections() {
  await dexieDB.updatedBookings.clear();
  await dexieDB.untypedBookings.clear();
  await dexieDB.logs.clear();
  await dexieDB.cashTransfers.clear();

  LogService.info(
    "Cleared updatedBookings, untypedBookings, logs and cashTransfers tables"
  );
}

/**
 * Reset DB helper method to be used in components.
 * If the reset is already running, it will not run another one.
 * If the reset is not allowed, it will retry 5 times.
 */
export async function resetGrowingCollecitonsFromUI() {
  // If a reset is already running, don't run another one
  if (resetRunning) {
    return;
  }

  resetRunning = true;

  // We will retry for 5 times, waiting retryTimeout ms between each retry
  const retryTimeout = 5000;
  let retries = 0;

  while (retries++ < 5) {
    // Reset the db if possible and break the loop
    if (canResetDatabase()) {
      try {
        await resetGrowingCollections();
        LogService.info("DB reset successfully");
        break;
      } catch (error) {
        LogService.info("Error resetting DB", { error }, LogLevel.Error);
      }
    }
    if (retries < 5) {
      LogService.info(`Cannot reset DB, will retry in ${retryTimeout} ms`);
    } else {
      LogService.info("Could not reset DB, stopping");
    }
    await Timeout.set(retryTimeout);
  }

  // This is no longer running;
  resetRunning = false;
}

/**
 * Log pending, ignored, and failed bookings in Dexie DB
 * @param afterClosingSales If true, appends "after closing sales" to log messages
 */
export async function logPendingIgnoredAndFailedBookings(
  afterClosingSales?: boolean
) {
  const context = afterClosingSales ? "after closing sales" : "";

  LogService.info(
    `Dexie: checking for pending, ignored, and failed bookings ${context}`
  );

  const statusesToCheck = [
    { status: SyncStatus.Pending, label: "pending" },
    { status: SyncStatus.Ignored, label: "ignored" },
    { status: SyncStatus.Failed, label: "failed" },
  ];

  for (const { status, label } of statusesToCheck) {
    let result: string[] = [];

    await dexieDB.untypedBookings
      .where("syncStatus")
      .equals(status)
      .each((order) => {
        result.push(order.uuid);
      });

    if (result.length > 0) {
      LogService.info(
        `Dexie: Warning - found ${
          result.length
        } ${label} untyped bookings, uuids: ${result.join(",")} ${context}`
      );
    }

    result = [];

    await dexieDB.updatedBookings
      .where("syncStatus")
      .equals(status)
      .each((order) => {
        result.push(order.uuid);
      });

    if (result.length > 0) {
      LogService.info(
        `Dexie: Warning - found ${
          result.length
        } ${label} updated bookings, uuids: ${result.join(",")} ${context}`
      );
    }
  }

  const untypedBookingsCount = await dexieDB.untypedBookings.count();
  const updatedBookingsCount = await dexieDB.updatedBookings.count();
  LogService.info(
    `Dexie: total number of untypedBookings: ${untypedBookingsCount} and updatedBookings: ${updatedBookingsCount} ${context}`
  );
}

/**
 * Clear Dexie db data
 */
export async function clearDexieDB() {
  LogService.info(
    "Dexie: clearing synced entities from Dexie db after closing sales"
  );
  try {
    await dexieDB.untypedBookings.clear();
    await dexieDB.updatedBookings.clear();
    // Delete synced transactions, unsynced ones are kept for retry
    await dexieDB.untypedTransactions
      .where("syncStatus")
      .equals(SyncStatus.Synced)
      .delete();
    LogService.info(
      `Dexie: cleared synced entities from Dexie db after closing sales`
    );
  } catch (error) {
    LogService.info(
      `Dexie: Error clearing Dexie db`,
      { params: error },
      LogLevel.Error
    );
  }
}
